/**
 * A3 — Pestaña «Comunicación» de la ficha: línea de tiempo de todo lo hablado con el
 * cliente por cualquier canal (correo sincronizado, llamadas, reuniones, WhatsApp, cartas…),
 * con filtros por canal, sentido y expediente, búsqueda de texto y registro manual.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Paperclip,
  Pencil,
  Plus,
  Search,
  Send,
  Trash2,
} from 'lucide-react'
import { api } from '../../lib/api'
import RegisterCommunicationModal from '../communications/RegisterCommunicationModal'
import SendEmailModal from '../communications/SendEmailModal'
import { CHANNEL_ICON, DirectionBadge, inputCls } from '../communications/common'
import {
  CommunicationAssignModeLabel,
  CommunicationChannelLabel,
  CommunicationDirectionLabel,
  type CaseListResponse,
  type CommunicationChannelKind,
  type CommunicationDirection,
  type CommunicationDto,
  type CommunicationListResponse,
} from '@toga/shared'

const PAGE_SIZE = 25

interface Props {
  clientId: string
  canWrite: boolean
  clientPhone?: string | null
  /** Avisa a la ficha de que cambió algo (insignia de la pestaña y resumen). */
  onChanged?: () => void
}

export default function ClientCommunicationsTab({
  clientId,
  canWrite,
  clientPhone,
  onChanged,
}: Props) {
  const [data, setData] = useState<CommunicationListResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [channel, setChannel] = useState<CommunicationChannelKind | ''>('')
  const [direction, setDirection] = useState<CommunicationDirection | ''>('')
  const [caseId, setCaseId] = useState('')
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [page, setPage] = useState(1)
  const [cases, setCases] = useState<CaseListResponse['items']>([])
  const [editing, setEditing] = useState<CommunicationDto | 'new' | null>(null)
  const [emailing, setEmailing] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300)
    return () => clearTimeout(t)
  }, [q])

  useEffect(() => {
    api
      .get<CaseListResponse>(`/cases?clientId=${clientId}&pageSize=100`)
      .then((r) => setCases(r.items))
      .catch(() => setCases([]))
  }, [clientId])

  // Al cambiar un filtro se vuelve a la primera página.
  useEffect(() => {
    setPage(1)
  }, [channel, direction, caseId, debouncedQ])

  const load = useCallback(async () => {
    setError(null)
    try {
      const p = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) })
      if (channel) p.set('channel', channel)
      if (direction) p.set('direction', direction)
      if (caseId) p.set('caseId', caseId)
      if (debouncedQ) p.set('q', debouncedQ)
      setData(await api.get<CommunicationListResponse>(`/clients/${clientId}/communications?${p}`))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar')
    }
  }, [clientId, page, channel, direction, caseId, debouncedQ])

  useEffect(() => {
    void load()
  }, [load, tick])

  const refresh = () => {
    setTick((t) => t + 1)
    onChanged?.()
  }

  const remove = async (c: CommunicationDto) => {
    if (!confirm('¿Borrar esta comunicación? Se elimina definitivamente.')) return
    try {
      await api.delete(`/communications/${c.id}`)
      refresh()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'No se pudo borrar')
    }
  }

  const counts = data?.channelCounts ?? {}
  const totalAll = Object.values(counts).reduce((a, n) => a + n, 0)
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1
  const filtered = !!(channel || direction || caseId || debouncedQ)

  return (
    <section className="surface overflow-hidden">
      <header className="px-5 py-3.5 border-b border-base flex flex-wrap items-center justify-between gap-3">
        <h3 className="t-primary font-semibold text-sm">Comunicación</h3>
        {canWrite && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setEmailing(true)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-base t-primary hover:bg-hover flex items-center gap-1.5"
            >
              <Send size={13} /> Enviar email
            </button>
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
            >
              <Plus size={13} /> Registrar comunicación
            </button>
          </div>
        )}
      </header>

      {notice && (
        <div
          role="status"
          className="px-5 py-2.5 border-b border-base text-xs text-emerald-300 bg-emerald-500/10 flex items-center justify-between gap-3"
        >
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} className="underline">
            Cerrar
          </button>
        </div>
      )}

      {/* Filtros */}
      <div className="px-5 py-3 border-b border-base space-y-3">
        <div className="flex flex-wrap gap-1.5">
          <button type="button" onClick={() => setChannel('')} className={chip(channel === '')}>
            Todos <span className="t-faint">{totalAll}</span>
          </button>
          {(Object.keys(CommunicationChannelLabel) as CommunicationChannelKind[])
            .filter((k) => (counts[k] ?? 0) > 0 || channel === k)
            .map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setChannel(k)}
                className={chip(channel === k)}
              >
                {CHANNEL_ICON[k]}
                {CommunicationChannelLabel[k]} <span className="t-faint">{counts[k] ?? 0}</span>
              </button>
            ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 t-faint" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar en asunto, resumen o contacto…"
              className={`${inputCls} pl-8`}
            />
          </div>
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value as CommunicationDirection | '')}
            className={`${inputCls} !w-auto`}
          >
            <option value="">Cualquier sentido</option>
            {(Object.keys(CommunicationDirectionLabel) as CommunicationDirection[]).map((d) => (
              <option key={d} value={d}>
                {CommunicationDirectionLabel[d]}
              </option>
            ))}
          </select>
          {cases.length > 0 && (
            <select
              value={caseId}
              onChange={(e) => setCaseId(e.target.value)}
              className={`${inputCls} !w-auto`}
            >
              <option value="">Todos los expedientes</option>
              {cases.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {error && (
        <div className="m-5 text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {!data && !error && (
        <div className="px-5 py-8 t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      )}
      {data && data.items.length === 0 && (
        <div className="px-5 py-10 text-center t-muted text-sm">
          {filtered
            ? 'Ninguna comunicación coincide con esos filtros.'
            : 'Todavía no hay comunicaciones con este cliente. El correo de sus direcciones entra solo; las llamadas, reuniones o WhatsApp se registran a mano.'}
        </div>
      )}

      {data && data.items.length > 0 && (
        <ul>
          {data.items.map((c) => (
            <li key={c.id} className="px-5 py-3 border-b border-base/50 last:border-b-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="t-faint">{CHANNEL_ICON[c.channel]}</span>
                <span className="t-primary text-sm font-medium truncate max-w-[60ch]">
                  {c.subject || CommunicationChannelLabel[c.channel]}
                </span>
                <DirectionBadge direction={c.direction} />
                {c.caseCode && (
                  <Link
                    to={`/cases/${c.caseId}`}
                    className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-toga-500/15 text-toga-200 border border-toga-500/30 hover:underline"
                  >
                    {c.caseCode}
                  </Link>
                )}
                {c.assignMode && c.assignMode !== 'MANUAL' && c.source === 'EMAIL' && (
                  <span
                    className="text-[10px] t-faint"
                    title={CommunicationAssignModeLabel[c.assignMode]}
                  >
                    {c.assignMode === 'AUTO' ? 'auto' : 'regla'}
                  </span>
                )}
                <span className="ml-auto t-faint text-xs whitespace-nowrap">
                  {new Date(c.occurredAt).toLocaleString('es-ES', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </span>
              </div>
              {(c.counterpartName || c.counterpartEmail || c.counterpartPhone) && (
                <div className="t-faint text-xs mt-0.5">
                  {c.direction === 'OUTBOUND'
                    ? 'A: '
                    : c.direction === 'INBOUND'
                      ? 'De: '
                      : 'Con: '}
                  {[c.counterpartName, c.counterpartEmail, c.counterpartPhone]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              )}
              {c.summary && (
                <div className="t-muted text-xs mt-1 whitespace-pre-line line-clamp-3">
                  {c.summary}
                </div>
              )}
              {c.documents.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {c.documents.map((d) => (
                    <span
                      key={d.id}
                      className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border border-base t-secondary"
                    >
                      <Paperclip size={10} /> {d.fileName}
                    </span>
                  ))}
                </div>
              )}
              {c.canEdit && canWrite && (
                <div className="flex gap-1 mt-1.5">
                  <button
                    type="button"
                    onClick={() => setEditing(c)}
                    className="text-[11px] px-2 py-1 rounded t-muted hover:bg-hover hover:text-toga-300 inline-flex items-center gap-1"
                  >
                    <Pencil size={11} /> Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => void remove(c)}
                    className="text-[11px] px-2 py-1 rounded t-muted hover:bg-rose-500/10 hover:text-rose-400 inline-flex items-center gap-1"
                  >
                    <Trash2 size={11} /> Borrar
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {data && data.total > data.pageSize && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-base text-sm">
          <span className="t-muted text-xs">
            Página {data.page} de {totalPages} · {data.total} comunicaciones
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="w-8 h-8 grid place-items-center rounded-lg border border-base t-secondary hover:bg-hover disabled:opacity-40"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="w-8 h-8 grid place-items-center rounded-lg border border-base t-secondary hover:bg-hover disabled:opacity-40"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {emailing && (
        <SendEmailModal
          clientId={clientId}
          onClose={() => setEmailing(false)}
          onSent={(registered) => {
            setEmailing(false)
            setNotice(
              registered
                ? 'Email enviado y registrado en la línea de tiempo.'
                : 'Email enviado, pero no se pudo registrar en la línea de tiempo: añádelo a mano con «Registrar comunicación».'
            )
            refresh()
          }}
        />
      )}

      {editing && (
        <RegisterCommunicationModal
          clientId={clientId}
          clientPhone={clientPhone}
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            refresh()
          }}
        />
      )}
    </section>
  )
}

function chip(active: boolean): string {
  return [
    'px-2.5 py-1 rounded-full text-xs border inline-flex items-center gap-1.5 transition',
    active
      ? 'border-toga-500/50 bg-toga-500/10 t-primary'
      : 'border-base t-secondary hover:bg-hover',
  ].join(' ')
}
